import { useQueryClient } from '@tanstack/react-query'
import { useCallback } from 'react'
import { keys } from '../../api/keys'
import { emit } from '../../platform/analytics/analytics'
import { useToast } from '../../platform/feedback/ToastProvider'
import type { PickExtras, PickerMerchant } from '../merchants/picker'
import type { MemorySuggestion, Txn } from './transactionWire'
import { failureOf, mayHaveCommitted, requestPatchAnswer, type SaveFailure } from './useAutosaveField'
import { useCategoryMemoryOffer } from './useCategoryMemoryOffer'
import { getTransaction } from './useTransaction'

type Outcome = { ok: true; suggestion: MemorySuggestion | null } | { ok: false; kind: SaveFailure }

/** What a row's merchant is called in a toast: the row's own embedded merchant, or "No merchant". */
const heldName = (t: Txn) => (t.merchantId === null ? 'No merchant' : (t.merchantBrief?.name ?? t.merchant))

/**
 * Set ONE row's merchant from a list (Transactions, Cashflow detail) — `useQuickCategory`, for merchants.
 *
 * The body is `merchant_id` alone. Both caches are invalidated: the transactions lists AND every cashflow
 * report, whose merchant breakdown and "no merchant" remainder move with it. "Most used" in the picker is
 * deliberately not (its own key root; a ranking of habits does not move with one edit).
 *
 * `next` is the merchant itself, not just its id: the picker hands over the whole choice, so the toast can
 * name it without a list lookup (the user may see more merchants than any page holds).
 *
 * Undo PATCHes back the id the ROW held when the user acted — `null` for a row with none — never a value
 * read later. The undo toast carries no Undo of its own.
 *
 * **The PATCH and its failure classification are the detail page's** (`requestPatch`, `failureOf`). Every
 * failure refreshes both caches. A refusal (4xx) did not commit: "Try again". A 5xx or no response may
 * have, so the row is re-read and the toast names what it holds — "set to" if the change landed, "It's
 * back to …" (the re-read row's own merchant) if not, "couldn't confirm" if the re-read fails too.
 * Analytics: `transaction_edited` / `transaction_edit_failed`, `field_kind: 'merchant'` — enums only, never
 * the merchant's id or name (rule 7).
 *
 * **A brand added with "Also set category"** (`extras`) sets the row's category in the same PATCH, and
 * Undo puts both back: the merchant and the category the row held (none: the option is offered only to
 * a row without one). That is a single-row category edit, so the server may answer with a learned-memory
 * offer (#753): shown beside the set toast, withdrawn by Undo. A merchant-only set never offers.
 */
export function useQuickMerchant() {
  const client = useQueryClient()
  const toast = useToast()
  const memory = useCategoryMemoryOffer('list')

  const patch = useCallback(async (id: string, merchant: string | null, category?: { id: string | null }): Promise<Outcome> => {
    let answer
    try {
      answer = await requestPatchAnswer(id, category === undefined ? { merchant_id: merchant } : { merchant_id: merchant, category_id: category.id })
    } catch (thrown) {
      const kind = failureOf(thrown)
      emit('transaction_edit_failed', { field_kind: 'merchant', error_kind: kind })
      return { ok: false, kind }
    }
    emit('transaction_edited', { field_kind: 'merchant' })
    return { ok: true, suggestion: category === undefined ? null : answer.memorySuggestion }
  }, [])

  const refresh = useCallback(
    () =>
      Promise.all([
        client.invalidateQueries({ queryKey: keys.transactions.all() }),
        client.invalidateQueries({ queryKey: keys.cashflow.all() }),
      ]),
    [client],
  )

  const set = useCallback(
    async (txn: Txn, next: PickerMerchant | null, extras?: PickExtras) => {
      const before = txn.merchantId
      const nextId = next?.id ?? null
      const category = extras === undefined ? undefined : { id: extras.categoryId }
      const categoryBefore = category === undefined ? undefined : { id: txn.categoryId }
      // Deliberately silent: nothing changed, so there is nothing to confirm or undo.
      if (before === nextId && category === undefined) return
      const outcome = await patch(txn.id, nextId, category)
      const suggestion = outcome.ok ? outcome.suggestion : null
      if (!outcome.ok) {
        // A refusal did not commit. A 5xx or a lost response may have: ask the server what the row holds.
        let held: Txn | 'unread' | undefined
        if (mayHaveCommitted(outcome.kind)) held = await getTransaction(txn.id).catch(() => 'unread' as const)
        await refresh()
        if (held === undefined) {
          toast.show({ kind: 'error', message: 'Couldn’t change the merchant. Try again.' })
          return
        }
        if (held === 'unread') {
          toast.show({ kind: 'error', message: 'Couldn’t confirm the merchant change. The list has been refreshed.' })
          return
        }
        if (held.merchantId !== nextId) {
          toast.show({ kind: 'error', message: `Couldn’t save the merchant. It’s back to ${heldName(held)}.` })
          return
        }
        // It committed: the row holds the new merchant, so this is a set like any other, with its Undo.
      } else {
        await refresh()
      }
      toast.show({
        kind: 'success',
        message: `${txn.merchant} set to ${next?.name ?? 'No merchant'}`,
        action: {
          label: 'Undo',
          onAct: () => {
            if (suggestion !== null) memory.withdraw(suggestion)
            void (async () => {
              const undone = await patch(txn.id, before, categoryBefore)
              await refresh()
              if (!undone.ok) toast.show({ kind: 'error', message: 'Couldn’t undo. The merchant is still changed.' })
            })()
          },
        },
      })
      memory.offer(suggestion)
    },
    [patch, refresh, toast, memory],
  )

  return { set }
}
